import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { PPTEditorStateType } from '@src/slide/domain/ui-model/constant/edit-state-type';
import { IRelativeProps } from '@src/slide/domain/ui-model/element-modeles/property/group-operation/group-condition';
import { enumerateGroup } from '@src/slide/domain/ui-model/element-modeles/property/group-operation/enumerate';
import type { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';

export interface ISizeInfo {
    width: number;
    height: number;
    left: number;
    top: number;
    centerX: number;
    centerY: number;
}

export interface IFlipInfo {
    flipH: number;
    flipV: number;
}

export default class ElementInfoAdapter {
    /**
     * 返回元素是否可选中
     */
    public static selectable(elementModel: IElementBaseModel): boolean {
        return elementModel.selectable
            && elementModel.currentEditState <= PPTEditorStateType.SELECTABLE;
    }

    /**
     * 返回元素是否可编辑
     */
    public static editable(elementModel: IElementBaseModel): boolean {
        return elementModel.currentEditState === PPTEditorStateType.EDITABLE;
    }
    /**
     * 返回元素是否可拖拽
     */
    public static draggable(elementModel: IElementBaseModel): boolean {
        return elementModel.currentEditState === PPTEditorStateType.EDITABLE;
    }

    /**
     * 返回元素frame信息
     * @param elementModel
     */
    public static sizeInfo(elementModel: IElementBaseModel): ISizeInfo {
        const elementStyle = elementModel.props.style;
        return {
            width: elementStyle.width,
            height: elementStyle.height,
            left: elementStyle.left,
            top: elementStyle.top,
            centerX: (elementStyle.width / 2) + elementStyle.left,
            centerY: (elementStyle.height / 2) + elementStyle.top,
        };
    }

    /**
     * 返回元素相对于slide的frame
     * @param elementModel
     */
    public static sizeInfoOnSlide(elementModel: IElementBaseModel): ISizeInfo {
        const frameOnSlide = elementModel.getFrame?.().frameOnSlide();
        return {
            width: frameOnSlide.width,
            height: frameOnSlide.height,
            left: frameOnSlide.left,
            top: frameOnSlide.top,
            centerX: (frameOnSlide.width / 2) + frameOnSlide.left,
            centerY: (frameOnSlide.height / 2) + frameOnSlide.top,
        };
    }

    /**
     * 返回元素翻转信息
     * @param elementModel
     */
    public static flipInfo(elementModel: IElementBaseModel): IFlipInfo {
        const elementStyle = elementModel.props.style;
        return {
            flipH: elementStyle.flipH === undefined ? 0 : elementStyle.flipH,
            flipV: elementStyle.flipV === undefined ? 0 : elementStyle.flipV,
        };
    }

    /**
     * 返回元素相对于Slide的翻转信息
     * @param elementModel
     */
    public static flipInfoOnSlide(elementModel: IElementBaseModel): IFlipInfo {
        const frameOnSlide = elementModel.getFrame?.().frameOnSlide();
        return {
            flipH: frameOnSlide.flipH === undefined ? 0 : frameOnSlide.flipH,
            flipV: frameOnSlide.flipV === undefined ? 0 : frameOnSlide.flipV,
        };
    }

    /**
     * 返回相对offset信息
     * @param elementModel
     * @param props
     * @param x
     * @param y
     */
    public static getOffsetWithRelative(
        props: IRelativeProps | null,
        x: number,
        y: number,
    ): { offsetX: number, offsetY: number} {
        let offsetX = x;
        let offsetY = y;
        if (props) {
            if (props.relativeRotate) {    // 外层角度修正
                const { relativeRotate } = props;
                const angle = (relativeRotate % 360) * Math.PI / 180;
                const cosAngle = Math.cos(angle);
                const sinAngle = Math.sin(angle);
                const relativeX = (offsetX * cosAngle) + (offsetY * sinAngle);
                const relativeY = (offsetY * cosAngle) - (offsetX * sinAngle);
                offsetX = relativeX;
                offsetY = relativeY;
            }
            if (props.relativeFlipH === 1) {   // 外层FlipH修正
                offsetX = -offsetX;
            }
            if (props.relativeFlipV === 1) {   // 外层FlipV修正
                offsetY = -offsetY;
            }
        }
        return {
            offsetX,
            offsetY,
        };
    }

    /**
     * 记录组合子元素的位置信息受父元素旋转的影响，应该是递增还是递减
     * 记录组合子元素的旋转角度受父元素翻转的影响，应该是顺时针改变还是逆时针改变
     */
    public static getGroupChildrenDirections(selectedModels: IElementBaseModel[]): number[] {
        return selectedModels.map((child) => {
            let res = 1;
            enumerateGroup(child, (groupModel: IGroupModel) => {
                const { flipH, flipV } = groupModel.getFrame().getFrameInfo();
                if (flipH !== flipV) {
                    res = res * -1;
                }
            });
            return res;
        });
    }
}
